import React from "react";
import { useQuery } from "react-query";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import cubeApiClient from "../../BusinessObjects/cubeApiService";
import { RoutePath } from "../../Utils/RoutePath";
import {
  Box,
  CircularProgress,
  Container,
  FilledInput,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  Typography,
  IconButton,
} from "@mui/material";
import axios from "axios";
import { useFormik } from "formik";
import * as yup from "yup";
import { LoadingButton } from "@mui/lab";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import notify from "devextreme/ui/notify";

type Props = {};

const ResetPasswordPage = (props: Props) => {
  let [searchParams, setSearchParams] = useSearchParams();
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);

  const navigate = useNavigate();

  const {
    data: isTokenValid,
    error: errorIsTokenValid,
    isLoading: isLoadingIsTokenValid,
    refetch: refetchIsTokenValid,
    isRefetching: isRefetchingIsTokenValid,
  } = useQuery(
    ["isTokenValid", searchParams.get("token")],
    () => {
      const service = new cubeApiClient();

      let token = searchParams.get("token");
      if (!token) throw new Error("Token not found");

      return service.checkResetPasswordToken(token);
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: false,
      onError(err) {
        console.log(err);
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      NewPassword: "",
      ConfirmPassword: "",
    },
    validationSchema: yup.object().shape({
      NewPassword: yup.string().required("Password obbligatoria"),
      ConfirmPassword: yup
        .string()
        .required("Conferma password obbligatoria")
        // @ts-ignore
        .oneOf([yup.ref("NewPassword")], "Le password non coincidono"),
    }),
    onSubmit: (values) => {
      let token = searchParams.get("token");
      if (!token) return;

      setLoading(true);

      const service = new cubeApiClient();

      service
        .resetPassword(token, values.NewPassword, values.ConfirmPassword)
        .then(
          (data) => {
            notify("Password modificata con successo", "success", 3000);
            navigate(RoutePath.LOGIN);
          },
          (err: any) => {
            setLoading(false);
          }
        );
    },
  });

  React.useEffect(() => {
    if (searchParams.get("token")) {
      refetchIsTokenValid();
    }
  }, [searchParams.get("token")]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  if (!searchParams.get("token")) return <Navigate to={RoutePath.LOGIN} />;

  if (isLoadingIsTokenValid) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (errorIsTokenValid) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography variant="h4" color="error">
          {errorIsTokenValid && axios.isAxiosError(errorIsTokenValid)
            ? errorIsTokenValid.response?.data?.MessageResult
            : "Ops! Qualcosa è andato storto"}
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <div className="w-100 d-flex justify-content-center align-items-center mt-5">
        <form className="login-form w-50" onSubmit={formik.handleSubmit}>
          <div className="form-group">
            <FormControl variant="filled" fullWidth margin="normal">
              <InputLabel htmlFor="NewPassword">New Password</InputLabel>
              <FilledInput
                id="NewPassword"
                name="NewPassword"
                type={showPassword ? "text" : "password"}
                value={formik.values.NewPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.NewPassword &&
                  Boolean(formik.errors.NewPassword)
                }
                disabled={loading}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                disableUnderline
              />
              {formik.touched.NewPassword &&
                Boolean(formik.errors.NewPassword) && (
                  <FormHelperText error>
                    {formik.errors.NewPassword}
                  </FormHelperText>
                )}
            </FormControl>
          </div>
          <div className="form-group">
            <FormControl variant="filled" fullWidth margin="normal">
              <InputLabel htmlFor="ConfirmPassword">
                Confirm Password
              </InputLabel>
              <FilledInput
                id="ConfirmPassword"
                name="ConfirmPassword"
                type={showPassword ? "text" : "password"}
                value={formik.values.ConfirmPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.ConfirmPassword &&
                  Boolean(formik.errors.ConfirmPassword)
                }
                disabled={loading}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                disableUnderline
              />
              {formik.touched.ConfirmPassword &&
                Boolean(formik.errors.ConfirmPassword) && (
                  <FormHelperText error>
                    {formik.errors.ConfirmPassword}
                  </FormHelperText>
                )}
            </FormControl>
          </div>
          <div className="form-group">
            <LoadingButton
              variant="contained"
              color="primary"
              size="large"
              disableElevation
              fullWidth
              style={{
                marginTop: 32,
                padding: 16,
                fontWeight: "bold",
                borderRadius: 20,
              }}
              type="submit"
              loading={loading}
            >
              Reset Password
            </LoadingButton>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default ResetPasswordPage;
